import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Box, Typography, Card, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Avatar, TextField,
    MenuItem, Select, FormControl, InputLabel, Stack, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, Alert, Snackbar, Divider, Tooltip,
    Checkbox, Tab, Tabs, Paper, LinearProgress, CircularProgress, alpha, Pagination,
} from "@mui/material";
import {
    Edit as EditIcon, AccessTime as ClockIcon, People as PeopleIcon,
    Search as SearchIcon, Warning as WarningIcon,
    Close as CloseIcon, CheckCircle as OkIcon,
    CalendarMonth as CalendarIcon, PictureAsPdf as ReportIcon,
    Download as DownloadIcon, Print as PrintIcon,
    PlayArrow as ActiveIcon, Today as TodayIcon,
    DateRange as WeekIcon, Business as FacilityIcon, RestartAlt as ResetIcon,
} from "@mui/icons-material";
import {
    format, differenceInMinutes, startOfWeek, eachDayOfInterval,
    startOfMonth, endOfMonth, isToday, subDays, addDays,
} from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { AttendanceStatus, AttendanceRecord } from "../types/attendance.types";
import attendanceService from "../services/attendanceService";
import masterDataService from "../services/masterDataService";
import facilityService from "../services/facilityService";
import { formatDurationHrsMins } from "../utils/dateUtils";

// ─── Local types ─────────────────────────────────────────────────────────────
type ViewMode = "today" | "weekly" | "monthly";

// ─── Generate rich sample data (Removed mock generation for production) ──────────────────────────
const STAFF = [
    { id: "cg-001", name: "Sophia Williams", role: "RN", avatar: "SW", color: "#7C3AED" },
    { id: "cg-002", name: "James Carter", role: "LPN", avatar: "JC", color: "#0077B6" },
    { id: "cg-003", name: "Maria Lopez", role: "CNA", avatar: "ML", color: "#D97706" },
    { id: "cg-004", name: "David Kim", role: "RN", avatar: "DK", color: "#16A34A" },
    { id: "cg-005", name: "Linda Johnson", role: "CNA", avatar: "LJ", color: "#0891B2" },
    { id: "cg-006", name: "Robert Harris", role: "LPN", avatar: "RH", color: "#BE185D" },
    { id: "cg-007", name: "Emily Chen", role: "RN", avatar: "EC", color: "#7C3AED" },
    { id: "cg-008", name: "Thomas Brown", role: "CNA", avatar: "TB", color: "#0077B6" },
];

const ALL_RECORDS: AttendanceRecord[] = [];

// ─── Helpers / config ────────────────────────────────────────────────────────
const STATUS_CFG: Record<AttendanceStatus, { label: string; bg: string; fg: string; chipBg: string }> = {
    CHECKED_IN: { label: "Active", bg: "#F0FDF4", fg: "#16A34A", chipBg: "#bbf7d0" },
    COMPLETED: { label: "Completed", bg: "#EFF6FF", fg: "#2563EB", chipBg: "#bfdbfe" },
    MISSED: { label: "Missed", bg: "#FEF2F2", fg: "#DC2626", chipBg: "#fecaca" },
    OFF: { label: "Day Off", bg: "#F8FAFC", fg: "#94A3B8", chipBg: "#e2e8f0" },
};

function calcHours(ci: string | null, co: string | null) {
    if (!ci || !co) return null;
    let mins = differenceInMinutes(new Date(co), new Date(ci));
    if (mins < 0) {
        mins += 24 * 60; // Handle overnight shifts
    }
    return parseFloat((mins / 60).toFixed(2));
}

function fmtTime(iso: string | null) {
    if (!iso) return "—";
    // If it's a local ISO string (no 'Z'), parse it as local time directly.
    return format(new Date(iso), "hh:mm a");
}

function staffColor(nameOrAvatar: string) {
    const colors = [
        { bg: "#ede9fe", fg: "#7c3aed" }, // purple
        { bg: "#e0f2fe", fg: "#0284c7" }, // blue
        { bg: "#ffedd5", fg: "#ea580c" }, // orange
        { bg: "#dcfce7", fg: "#16a34a" }, // green
        { bg: "#cffafe", fg: "#0891b2" }, // cyan
        { bg: "#fce7f3", fg: "#db2777" }  // pink
    ];
    let hash = 0;
    for (let i = 0; i < nameOrAvatar.length; i++) hash = nameOrAvatar.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
}

function getShift(checkInIso: string | null): "Morning" | "Afternoon" | "Night" | null {
    if (!checkInIso) return null;
    const str = checkInIso;
    const hour = new Date(str).getHours();
    if (hour >= 6 && hour < 14) return "Morning";
    if (hour >= 14 && hour < 22) return "Afternoon";
    return "Night";
}

function formatRoleForDisplay(role: string): string {
    if (!role) return 'Staff';
    if (role.toUpperCase() === 'NURSE' || role.toUpperCase() === 'CAREGIVER') return 'Caregiver';
    if (role.toUpperCase() === 'FACILITY_ADMIN' || role.toUpperCase() === 'FACILITY_MANAGER') return 'Facility Manager';

    return role
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AttendanceManagementPage: React.FC = () => {
    const [allRecords, setAllRecords] = useState<AttendanceRecord[]>([]);
    const [view, setView] = useState<ViewMode>("today");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<AttendanceStatus | "ALL" | "AVG">("ALL");
    const [facilityFilter, setFacilityFilter] = useState<string>("ALL");
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [selected, setSelected] = useState<string[]>([]); // record ids
    const [loading, setLoading] = useState(true);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);
    const [exportExcelLoading, setExportExcelLoading] = useState(false);
    const [dynamicFacilityOptions, setDynamicFacilityOptions] = useState<string[]>([]);
    const [facilityIdMap, setFacilityIdMap] = useState<Record<string, string>>({});

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editRecs, setEditRecs] = useState<AttendanceRecord[]>([]); // single or bulk
    const [editForm, setEditForm] = useState({ checkIn: "", checkOut: "", status: "COMPLETED" as AttendanceStatus, date: "" });
    const [statusOptions, setStatusOptions] = useState<{ id: string; name: string }[]>([]);

    // Report dialog
    const [reportOpen, setReportOpen] = useState(false);
    const [reportFrom, setReportFrom] = useState<Date | null>(null);
    const [reportTo, setReportTo] = useState<Date | null>(null);
    const [reportFacility, setReportFacility] = useState<string>("");

    // Snackbar
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });

    // ── Fetch Data ────────────────────────────────────────────────────────────
    const fetchAttendanceData = useCallback(async () => {
        try {
            setLoading(true);
            let startDateStr = '';
            let endDateStr = '';

            if (view === "today") {
                startDateStr = format(selectedDate, "yyyy-MM-dd");
                endDateStr = format(selectedDate, "yyyy-MM-dd");
            } else if (view === "weekly") {
                const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
                const end = addDays(start, 6);
                startDateStr = format(start, "yyyy-MM-dd");
                endDateStr = format(end, "yyyy-MM-dd");
            } else if (view === "monthly") {
                const start = startOfMonth(selectedDate);
                const end = endOfMonth(selectedDate);
                startDateStr = format(start, "yyyy-MM-dd");
                endDateStr = format(end, "yyyy-MM-dd");
            }

            const params: any = {
                startDate: startDateStr,
                endDate: endDateStr,
            };

            const response = await attendanceService.listByFacilityAndDate(params);

            const dataToMap = response?.data || response || [];
            if (Array.isArray(dataToMap)) {
                const mappedRecords: AttendanceRecord[] = dataToMap.map((r: any) => {
                    const originalRole = r.role || r.caregiver?.role || 'Staff';
                    const displayRole = formatRoleForDisplay(originalRole);
                        let th = typeof r.totalHours === 'number' ? r.totalHours : null;
                        if (th !== null && th < 0) {
                            th += 24;
                        }

                        return {
                            id: r.id || `att-${Math.random()}`,
                            caregiverId: r.caregiverId || `cg-${Math.random()}`,
                            caregiverName: r.caregiverName || r.caregiver?.name || 'Unknown Staff',
                            role: displayRole,
                            avatar: r.avatar || (r.caregiverName ? r.caregiverName.substring(0, 2).toUpperCase() : 'U'),
                            facilityId: r.facilityId || r.facility?.id,
                            facilityName: r.facilityName || r.facility?.name || 'Unknown Facility',
                            checkIn: r.checkIn || null,
                            checkOut: r.checkOut || null,
                            totalHours: th,
                        shift: getShift(r.checkIn),
                        status: (r.status as string)?.toUpperCase() || 'COMPLETED',
                        date: r.date || (r.checkIn ? format(new Date(r.checkIn), "yyyy-MM-dd") : startDateStr),
                    };
                });
                // We overwrite ALL_RECORDS completely so it only shows real data
                setAllRecords(mappedRecords.length > 0 ? mappedRecords : []);
            }
        } catch (error) {
            console.error("Failed to fetch attendance records:", error);
            setSnack({ open: true, msg: "Failed to load attendance data", sev: "error" });
        } finally {
            setLoading(false);
        }
    }, [selectedDate, view]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    useEffect(() => {
        const loadFacilities = async () => {
            try {
                const response = await facilityService.getAllFacilities(0, 100);
                // The API returns a PageResponse<FacilityDTO>
                const facilityNames = response.content.map(f => f.name).filter(Boolean);
                const idMap: Record<string, string> = {};
                response.content.forEach(f => {
                    if (f.name && f.id) idMap[f.name] = f.id;
                });
                setFacilityIdMap(idMap);
                setDynamicFacilityOptions(facilityNames);
            } catch (err) {
                console.error("Failed to load facility options:", err);
            }
        };
        loadFacilities();

        const loadStatuses = async () => {
            try {
                const lookupValues = await masterDataService.getActiveLookupValuesByName('AttendanceStatus');
                setStatusOptions(lookupValues.map(v => ({ id: v.id, name: v.valueName })));
            } catch (err) {
                console.error("Failed to load attendance status options:", err);
            }
        };
        loadStatuses();
    }, []);

    // ── Computed ──────────────────────────────────────────────────────────────
    const selDateStr = format(selectedDate, "yyyy-MM-dd");

    // Derive unique staff from all records
    const dynamicStaff = useMemo(() => {
        const staffMap = new Map();
        allRecords.forEach(r => {
            if (!staffMap.has(r.caregiverId)) {
                staffMap.set(r.caregiverId, {
                    id: r.caregiverId,
                    name: r.caregiverName,
                    role: r.role,
                    avatar: r.avatar,
                    color: staffColor(r.caregiverName)
                });
            }
        });
        return Array.from(staffMap.values()).sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [allRecords]);

    // Derive unique facility names for filter dropdowns
    const facilityOptions = useMemo(() => {
        const set = new Set(allRecords.map(r => r.facilityName));
        dynamicFacilityOptions.forEach(f => set.add(f));
        return Array.from(set).filter(Boolean).sort();
    }, [allRecords, dynamicFacilityOptions]);

    // Selected-date records (Today tab navigates with the date picker)
    const selDateRecords = useMemo(() =>
        allRecords.filter(r => r.date === selDateStr), [allRecords, selDateStr]);

    const stats = useMemo(() => {
        const tr = facilityFilter === "ALL"
            ? selDateRecords
            : selDateRecords.filter(r => r.facilityName === facilityFilter);
        return {
            total: dynamicStaff.length,
            active: tr.filter(r => r.status === "CHECKED_IN").length,
            completed: tr.filter(r => r.status === "COMPLETED").length,
            missed: tr.filter(r => r.status === "MISSED").length,
            avgHours: (() => {
                const hrs = tr.filter(r => r.totalHours).map(r => r.totalHours!);
                return hrs.length ? (hrs.reduce((a, b) => a + b, 0) / hrs.length) : null;
            })(),
        };
    }, [selDateRecords, facilityFilter]);

    // Today-tab filtered rows (uses selectedDate, not hardcoded today)
    const todayFiltered = useMemo(() => selDateRecords.filter(r => {
        const ms = !search || r.caregiverName.toLowerCase().includes(search.toLowerCase()) || r.role.toLowerCase().includes(search.toLowerCase());
        const mf = statusFilter === "ALL" || 
                   (statusFilter === "AVG" ? (r.totalHours != null && r.totalHours > 0) : r.status === statusFilter);
        const mfac = facilityFilter === "ALL" || r.facilityName === facilityFilter;
        return ms && mf && mfac;
    }), [selDateRecords, search, statusFilter, facilityFilter]);

    const totalPages = Math.ceil(todayFiltered.length / rowsPerPage);
    const pagedRows = todayFiltered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    // Weekly grid
    const weekDays = useMemo(() => {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        return eachDayOfInterval({ start, end: addDays(start, 6) });
    }, [selectedDate]);

    // Monthly grid
    const monthDays = useMemo(() => {
        const start = startOfMonth(selectedDate);
        const end = endOfMonth(selectedDate);
        return eachDayOfInterval({ start, end });
    }, [selectedDate]);

    const getRecord = useCallback((caregiverId: string, date: Date) => {
        const rec = allRecords.find(r =>
            r.caregiverId === caregiverId && r.date === format(date, "yyyy-MM-dd")
        ) || null;
        if (!rec) return null;
        if (facilityFilter !== "ALL" && rec.facilityName !== facilityFilter) return null;
        return rec;
    }, [allRecords, facilityFilter]);

    // ── Bulk selection ────────────────────────────────────────────────────────
    // Day-Off rows cannot be selected; COMPLETED and MISSED are correctable
    const selectableRows = useMemo(() => pagedRows.filter(r => r.status !== "OFF"), [pagedRows]);
    const allPageSelected = selectableRows.length > 0 && selectableRows.every(r => selected.includes(r.id));
    const someSelected = selected.length > 0;

    // Reset to page 1 when filters change
    React.useEffect(() => { setPage(1); }, [search, statusFilter, facilityFilter, view, selectedDate]);

    const toggleSelect = (id: string) =>
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const toggleSelectAll = () =>
        allPageSelected
            ? setSelected(prev => prev.filter(id => !selectableRows.find(r => r.id === id)))
            : setSelected(prev => Array.from(new Set([...prev, ...selectableRows.map(r => r.id)])));

    // ── Apply edits to one or many records ───────────────────────────────────
    const applyEdit = async (ids: string[]) => {
        try {
            setLoading(true);
            const isNoTime = editForm.status === "OFF" || editForm.status === "MISSED";
            const ci = (!isNoTime && editForm.checkIn) ? new Date(editForm.checkIn).toISOString() : "";
            const co = (!isNoTime && editForm.checkOut) ? new Date(editForm.checkOut).toISOString() : "";

            const recsToUpdate = allRecords.filter(r => ids.includes(r.id));

            if (ids.length > 1 && recsToUpdate.length > 0) {
                if (!editForm.status || !editForm.checkIn || !editForm.checkOut) {
                    setSnack({ open: true, msg: "Status, Check-In and Check-Out times are required.", sev: "error" });
                    setLoading(false);
                    return;
                }
                await attendanceService.bulkUpdate({
                    date: editForm.date || recsToUpdate[0].date,
                    caregiverIds: recsToUpdate.map(r => r.caregiverId),
                    status: editForm.status,
                    checkInTime: ci,
                    checkOutTime: co
                });
            } else if (recsToUpdate.length === 1) {
                if (!editForm.status || !editForm.checkIn || !editForm.checkOut) {
                    setSnack({ open: true, msg: "Status, Check-In and Check-Out times are required.", sev: "error" });
                    setLoading(false);
                    return;
                }
                const rec = recsToUpdate[0];
                const updatedDate = editForm.date || rec.date;
                await attendanceService.updateRecord({
                    caregiverId: rec.caregiverId,
                    facilityId: rec.facilityId || "",
                    date: updatedDate,
                    status: editForm.status,
                    checkIn: ci,
                    checkOut: co
                });
            }

            setAllRecords(prev => prev.map(r =>
                ids.includes(r.id) ? {
                    ...r,
                    checkIn: ci || null,
                    checkOut: co || null,
                    totalHours: calcHours(ci || null, co || null),
                    status: editForm.status,
                    date: editForm.date || r.date
                } : r
            ));
            setEditOpen(false);
            setSelected([]);
            setSnack({ open: true, msg: `Updated ${ids.length} record${ids.length > 1 ? "s" : ""} successfully.`, sev: "success" });
        } catch (error: any) {
            console.error("Failed to update records:", error);
            setSnack({ open: true, msg: error.response?.data?.message || "Failed to update attendance records.", sev: "error" });
        } finally {
            setLoading(false);
        }
    };

    const openSingleEdit = (rec: AttendanceRecord) => {
        setEditRecs([rec]);

        const ci = rec.checkIn;
        const co = rec.checkOut;

        const dateToUse = rec.date || format(new Date(), "yyyy-MM-dd");
        setEditForm({
            checkIn: ci ? format(new Date(ci), "yyyy-MM-dd'T'HH:mm") : `${dateToUse}T09:00`,
            checkOut: co ? format(new Date(co), "yyyy-MM-dd'T'HH:mm") : `${dateToUse}T17:00`,
            status: rec.status?.toUpperCase() || "COMPLETED",
            date: dateToUse,
        });
        setEditOpen(true);
    };

    const openBulkEdit = () => {
        const recs = allRecords.filter(r => selected.includes(r.id));
        setEditRecs(recs);
        const dateStr = recs.length > 0 ? recs[0].date : format(new Date(), "yyyy-MM-dd");
        setEditForm({
            checkIn: `${dateStr}T09:00`,
            checkOut: `${dateStr}T17:00`,
            status: "COMPLETED",
            date: dateStr
        });
        setEditOpen(true);
    };

    // ── Report generation ─────────────────────────────────────────────────────
    const reportRecords = useMemo(() => {
        const fromStr = reportFrom ? format(reportFrom, "yyyy-MM-dd") : "";
        const toStr = reportTo ? format(reportTo, "yyyy-MM-dd") : "";
        return allRecords.filter(r =>
            r.date >= fromStr && r.date <= toStr &&
            (reportFacility === "ALL" || r.facilityName === reportFacility)
        );
    }, [allRecords, reportFrom, reportTo, reportFacility]);

    const reportStats = useMemo(() => {
        const byStaff = dynamicStaff.map((s: any) => {
            const recs = reportRecords.filter(r => r.caregiverId === s.id);
            const worked = recs.filter(r => r.status !== "MISSED" && r.status !== "OFF");
            const totalH = recs.reduce((a, r) => a + (r.totalHours || 0), 0);
            return {
                ...s, days: recs.length,
                completed: recs.filter(r => r.status === "COMPLETED").length,
                missed: recs.filter(r => r.status === "MISSED").length,
                totalHours: totalH.toFixed(1),
                adherencePct: recs.length ? Math.round((worked.length / recs.length) * 100) : 0,
            };
        });
        return byStaff;
    }, [reportRecords]);

    const handleExportExcel = async () => {
        try {
            setExportExcelLoading(true);
            const fromStr = reportFrom ? format(reportFrom, "yyyy-MM-dd") : undefined;
            const toStr = reportTo ? format(reportTo, "yyyy-MM-dd") : undefined;

            let facilityId: string | undefined = undefined;
            const facilityMapping: Record<string, string> = {
                "Clover Home": "34c13c76-204d-43d5-90e3-56a50cfc4e84",
                "Lakewood Villa": "383d7ced-61dd-4a20-88cf-7316bd6b6d66",
            };

            if (reportFacility && reportFacility !== "ALL") {
                const row = allRecords.find(r => r.facilityName === reportFacility);
                facilityId = (row && row.facilityId) ? row.facilityId : (facilityIdMap[reportFacility] || facilityMapping[reportFacility]);
            }

            const blob = await attendanceService.downloadReportExcel(fromStr, toStr, facilityId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance_report_${fromStr || "all"}_${toStr || "all"}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setSnack({ open: true, msg: "Report exported successfully", sev: "success" });
            setReportOpen(false);
        } catch (err) {
            console.error("Failed to export report:", err);
            setSnack({ open: true, msg: "Failed to export report", sev: "error" });
        } finally {
            setExportExcelLoading(false);
        }
    };

    const handleExportPdf = async () => {
        try {
            setExportPdfLoading(true);
            const fromStr = reportFrom ? format(reportFrom, "yyyy-MM-dd") : undefined;
            const toStr = reportTo ? format(reportTo, "yyyy-MM-dd") : undefined;

            let facilityId: string | undefined = undefined;
            const facilityMapping: Record<string, string> = {
                "Clover Home": "34c13c76-204d-43d5-90e3-56a50cfc4e84",
                "Lakewood Villa": "383d7ced-61dd-4a20-88cf-7316bd6b6d66",
            };

            if (reportFacility && reportFacility !== "ALL") {
                const row = allRecords.find(r => r.facilityName === reportFacility);
                facilityId = (row && row.facilityId) ? row.facilityId : (facilityIdMap[reportFacility] || facilityMapping[reportFacility]);
            }

            const blob = await attendanceService.downloadReportPdf(fromStr, toStr, facilityId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance_report_${fromStr || "all"}_${toStr || "all"}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setSnack({ open: true, msg: "Report PDF exported successfully", sev: "success" });
            setReportOpen(false);
        } catch (err) {
            console.error("Failed to export report PDF:", err);
            setSnack({ open: true, msg: "Failed to export report PDF", sev: "error" });
        } finally {
            setExportPdfLoading(false);
        }
    };


    // ── Status Cell for calendar views ───────────────────────────────────────
    const StatusDot = ({ rec, onClick }: { rec: AttendanceRecord | null; onClick?: () => void }) => {
        if (!rec) return <Box sx={{ width: 28, height: 28, mx: "auto" }} />;
        const cfg = STATUS_CFG[rec.status];
        return (
            <Tooltip title={`${rec.status === "CHECKED_IN" ? "Active" : fmtTime(rec.checkIn) + " – " + fmtTime(rec.checkOut)}`} arrow>
                <Box onClick={onClick}
                    sx={{
                        width: 28, height: 28, borderRadius: "8px", bgcolor: cfg.chipBg, border: `2px solid ${cfg.fg}22`,
                        display: "flex", alignItems: "center", justifyContent: "center", mx: "auto",
                        cursor: onClick ? "pointer" : "default", transition: "transform 0.15s",
                        "&:hover": onClick ? { transform: "scale(1.15)" } : {}
                    }}>
                    <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 800, color: cfg.fg }}>
                        {rec.status === "CHECKED_IN" ? "●" : rec.status === "COMPLETED" ? "✓" : rec.status === "MISSED" ? "✗" : "–"}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    // ═════════════════════════════════════════════════════════════════════════
    return (
        <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 2, pt: 3, }}>
            {/* Snackbar */}
            <Snackbar open={snack.open} autoHideDuration={4000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snack.sev} sx={{ borderRadius: "10px" }}>{snack.msg}</Alert>
            </Snackbar>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ mb: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: { xs: 1, sm: 0 }, flex: 1, minWidth: 0 }}>
                    <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box className="material-symbols-rounded" sx={{ fontSize: 36, color: "#48CAE4" }}>calendar_month</Box>
                            <Box
                                className="material-symbols-rounded"
                                sx={{
                                    position: 'absolute',
                                    fontSize: 16,
                                    bottom: -4,
                                    right: -4,
                                    color: 'white',
                                    backgroundColor: '#48CAE4',
                                    borderRadius: '50%',
                                    width: 22,
                                    height: 22,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid white'
                                }}
                            >
                                schedule
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h5"
                            fontWeight="700"
                            sx={{ mb: 0.5, color: "#1A2B45", whiteSpace: { xs: "normal", sm: "nowrap", md: "normal" }, overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            Attendance Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem", whiteSpace: "normal" }}>
                            Monitor staff attendance, track work hours, and generate comprehensive reports
                        </Typography>
                    </Box>
                </Box>
                <Button variant="contained" startIcon={<ReportIcon />} onClick={() => { setReportFrom(null); setReportTo(null); setReportFacility(""); setReportOpen(true); }}
                    sx={{
                        borderRadius: "12px", textTransform: "none", fontWeight: "normal", boxShadow: "none",
                        bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" },
                        width: { xs: "100%", sm: "auto" },
                        ml: { sm: "auto" }
                    }}>
                    Generate Report
                </Button>
            </Box>

            {/* ── Stats Cards ─────────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {[
                    { label: "Total Staff", value: stats.total, color: "#00B4D8", bg: "#E0F2FE", icon: <PeopleIcon />, filterValue: "ALL" },
                    { label: "Active Now", value: stats.active, color: "#34d399", bg: "#F0FDF4", icon: <ActiveIcon />, filterValue: "CHECKED_IN" },
                    { label: "Completed Today", value: stats.completed, color: "#60a5fa", bg: "#EFF6FF", icon: <OkIcon />, filterValue: "COMPLETED" },
                    { label: "Missed / Absent", value: stats.missed, color: "#f87171", bg: "#FEF2F2", icon: <WarningIcon />, filterValue: "MISSED" },
                    { label: "Avg. Hours Today", value: formatDurationHrsMins(stats.avgHours), color: "#a78bfa", bg: "#F5F3FF", icon: <ClockIcon />, filterValue: 'AVG' },
                ].map((s, index) => {
                    const isActive = s.filterValue && statusFilter === s.filterValue;
                    return (
                        <Grid item xs={index === 4 ? 12 : 6} sm={index > 2 ? 6 : 4} lg={2.4} key={s.label}>
                            <Card elevation={0} onClick={() => { if (s.filterValue) { setStatusFilter(s.filterValue as any); setPage(0); } }} sx={{
                                p: { xs: 1.5, md: 1, lg: 2.5 },
                                borderRadius: "16px",
                                border: "1px solid",
                                borderColor: isActive ? s.color : "divider",
                                bgcolor: isActive ? alpha(s.color, 0.04) : '#fff',
                                transition: 'all 0.2s',
                                cursor: s.filterValue ? 'pointer' : 'default',
                                '&:hover': {
                                    boxShadow: `0 4px 16px ${alpha(s.color, 0.15)}`,
                                    borderColor: isActive ? s.color : alpha(s.color, 0.5)
                                }
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexDirection: "row", textAlign: "left" }}>
                                    <Box sx={{ p: 1.25, borderRadius: "12px", bgcolor: `${s.color}20`, color: s.color, display: "flex" }}>
                                        {React.cloneElement(s.icon as React.ReactElement, { sx: { fontSize: 28 } })}
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: s.color, lineHeight: 1, fontSize: "1.75rem" }}>
                                            {s.value}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                                            {s.label}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* ── View Switcher Tabs ───────────────────────────────────────── */}
            <Card elevation={0} sx={{ borderRadius: "20px", border: "1px solid", borderColor: "divider", overflow: "hidden", position: "relative" }}>
                {loading && (
                    <Box sx={{
                        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: "rgba(255, 255, 255, 0.7)", zIndex: 10,
                        backdropFilter: "blur(2px)"
                    }}>
                        <CircularProgress size={40} sx={{ color: "#0077B6" }} />
                    </Box>
                )}
                <Box sx={{
                    borderBottom: "1px solid", borderColor: "divider", bgcolor: "#F8FAFC",
                    px: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1
                }}>
                    <Tabs value={view} onChange={(_, v) => { setView(v); setSelected([]); }}
                        sx={{
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.85rem", minHeight: 50 },
                            "& .Mui-selected": { color: "#0077B6" }, "& .MuiTabs-indicator": { bgcolor: "#0077B6" }
                        }}>
                        <Tab value="today" label="Today" icon={<TodayIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                        <Tab value="weekly" label="Weekly" icon={<WeekIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                        <Tab value="monthly" label="Monthly" icon={<CalendarIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                    </Tabs>

                    {/* Date navigator */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Button size="small" onClick={() => setSelectedDate(d => subDays(d, view === "monthly" ? 30 : view === "weekly" ? 7 : 1))}
                            sx={{ minWidth: 32, borderRadius: "8px", textTransform: "none" }}>◀</Button>
                        <Typography variant="body2" fontWeight={700} sx={{ color: "#1A2B45", minWidth: 130, textAlign: "center" }}>
                            {view === "today" ? format(selectedDate, "MMMM d, yyyy") :
                                view === "weekly" ? `${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "MMM d")} – ${format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 6), "MMM d, yyyy")}` :
                                    format(selectedDate, "MMMM yyyy")}
                        </Typography>
                        <Button size="small" onClick={() => setSelectedDate(d => addDays(d, view === "monthly" ? 30 : view === "weekly" ? 7 : 1))}
                            sx={{ minWidth: 32, borderRadius: "8px", textTransform: "none" }}>▶</Button>
                    </Stack>
                </Box>

                {/* ══════════════════ TODAY VIEW ══════════════════ */}
                {view === "today" && (
                    <>
                        {/* Filters + Bulk actions */}
                        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField placeholder="Search by name / role" value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                InputProps={{ startAdornment: <SearchIcon sx={{ color: "#94A3B8", mr: 0.75, fontSize: 17 }} /> }}
                                sx={{ flex: 1, minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                            <FormControl sx={{ minWidth: 140 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter} label="Status"
                                    onChange={e => { setStatusFilter(e.target.value as any); setPage(1); }}
                                    sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="ALL">All Status</MenuItem>
                                    {(Object.entries(STATUS_CFG) as [AttendanceStatus, any][]).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>{v.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 150 }}>
                                <InputLabel sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <FacilityIcon sx={{ fontSize: 14 }} /> Facility
                                </InputLabel>
                                <Select value={facilityFilter} label="Facility"
                                    onChange={e => { setFacilityFilter(e.target.value); setPage(1); }}
                                    sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="ALL">All Facilities</MenuItem>
                                    {facilityOptions.map(f => (
                                        <MenuItem key={f} value={f}>{f}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {someSelected && (
                                <>
                                    <Typography variant="caption" sx={{ color: "#0077B6", fontWeight: 700 }}>
                                        {selected.length} selected
                                    </Typography>
                                    <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                        onClick={openBulkEdit}
                                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, color: "#0077B6", borderColor: "#0077B6" }}>
                                        Bulk Edit
                                    </Button>
                                    <Button size="small" startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setSelected([])}
                                        sx={{ borderRadius: "8px", textTransform: "none", color: "#5C6F85" }}>
                                        Clear
                                    </Button>
                                </>
                            )}
                            <Tooltip title="Reset all filters">
                                <IconButton
                                    onClick={() => { setSearch(""); setStatusFilter("ALL"); setFacilityFilter("ALL"); setPage(1); }}
                                    size="medium"
                                    sx={{
                                        ml: "auto",
                                        flexShrink: 0,
                                        color: 'text.secondary',
                                        transition: 'color 0.2s',
                                        '&:hover': {
                                            color: 'error.main',
                                            bgcolor: 'transparent',
                                        },
                                    }}
                                >
                                    <ResetIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box sx={{ overflowX: "auto", width: "100%" }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 800 }}>
                                    <TableHead sx={{
                                        "& .MuiTableCell-head": {
                                            color: "#434445ff !important",
                                        }
                                    }}>
                                        <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={allPageSelected} indeterminate={someSelected && !allPageSelected}
                                                    onChange={toggleSelectAll} />
                                            </TableCell>
                                            {["Users", "Role", "Facility", "Check-In", "Check-Out", "Total Duration", "Status", "Edit"].map(h => (
                                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.73rem", color: "#5C6F85", textTransform: "uppercase", letterSpacing: "0.04em", py: 1.5 }}>
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedRows.map(rec => {
                                            const cfg = STATUS_CFG[rec.status] || { label: rec.status, bg: "#F8FAFC", fg: "#94A3B8", chipBg: "#e2e8f0" };
                                            const isSelected = selected.includes(rec.id);
                                            return (
                                                <TableRow key={rec.id} hover selected={isSelected}
                                                    sx={{ "&.Mui-selected": { bgcolor: "rgba(0,119,182,0.04)" }, "&:last-child td": { borderBottom: 0 } }}>
                                                    <TableCell padding="checkbox">
                                                        {rec.status !== "OFF" && (
                                                            <Checkbox size="small" checked={isSelected} onChange={() => toggleSelect(rec.id)} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: staffColor(rec.avatar).bg, color: staffColor(rec.avatar).fg, fontSize: "0.7rem", fontWeight: 700 }}>
                                                                {rec.avatar}
                                                            </Avatar>
                                                            {rec.caregiverName}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{rec.role}</TableCell>
                                                    <TableCell>{rec.facilityName}</TableCell>
                                                    <TableCell>
                                                        <Chip label={fmtTime(rec.checkIn)} size="small"
                                                            sx={{ bgcolor: rec.checkIn ? "#F0FDF4" : "#F8FAFC", color: rec.checkIn ? "#16A34A" : "#94A3B8" }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {rec.status === "CHECKED_IN"
                                                            ? <Chip label="On-shift" size="small" sx={{ bgcolor: "#FFF7ED", color: "#D97706" }} />
                                                            : <Chip label={fmtTime(rec.checkOut)} size="small"
                                                                sx={{ bgcolor: rec.checkOut ? "#FEF2F2" : "#F8FAFC", color: rec.checkOut ? "#DC2626" : "#94A3B8", }} />
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {rec.totalHours != null ? (
                                                            <Chip
                                                                icon={<ClockIcon sx={{ fontSize: '14px !important' }} />}
                                                                label={formatDurationHrsMins(rec.totalHours)}
                                                                size="small"
                                                                sx={{ bgcolor: "#F5F3FF", color: "#a78bfa", fontWeight: 700, '& .MuiChip-icon': { color: "#a78bfa" } }}
                                                            />
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={cfg.label} size="small"
                                                            sx={{ bgcolor: cfg.bg, color: cfg.fg, fontWeight: 600 }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Edit record" arrow>
                                                            <IconButton size="small" onClick={() => openSingleEdit(rec)}
                                                                sx={{ color: "#0077B6", bgcolor: "#E0F2FE", borderRadius: "7px", "&:hover": { bgcolor: "#BAE6FD" } }}>
                                                                <EditIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {pagedRows.length === 0 && (
                                            <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 5, color: "#94A3B8" }}>No records found.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {pagedRows.length} of {todayFiltered.length} entries
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                variant="outlined"
                                shape="rounded"
                                size="small"
                            />
                        </Box>
                    </>
                )}

                {/* ══════════════════ WEEKLY VIEW ══════════════════ */}
                {view === "weekly" && (
                    <Box sx={{ overflowX: "auto" }}>
                        {/* Legend */}
                        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {(Object.entries(STATUS_CFG) as any[]).map(([k, v]: [AttendanceStatus, typeof STATUS_CFG[AttendanceStatus]]) => (
                                <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: v.chipBg, border: `1.5px solid ${v.fg}` }} />
                                    <Typography variant="caption" sx={{ color: "#5C6F85", fontWeight: 600, fontSize: "0.68rem" }}>{v.label}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Table size="small">
                            <TableHead sx={{
                                "& .MuiTableCell-head": {
                                    color: "#434445ff !important",
                                    fontSize: "1rem !important",
                                    fontWeight: "600 !important"
                                }
                            }}>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: "0.73rem", color: "#5C6F85", textTransform: "uppercase", minWidth: 160 }}>Users</TableCell>
                                    {weekDays.map(d => (
                                        <TableCell key={d.toString()} align="center"
                                            sx={{
                                                fontWeight: 700, fontSize: "0.73rem", color: isToday(d) ? "#0077B6" : "#5C6F85",
                                                textTransform: "uppercase", minWidth: 70,
                                                borderRadius: isToday(d) ? "8px 8px 0 0" : 0,
                                                bgcolor: isToday(d) ? "rgba(0,119,182,0.06)" : "transparent"
                                            }}>
                                            <Box>{format(d, "EEE")}</Box>
                                            <Box sx={{ fontSize: "1rem", fontWeight: 800, color: isToday(d) ? "#0077B6" : "#1A2B45" }}>{format(d, "d")}</Box>
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.73rem", color: "#5C6F85", textTransform: "uppercase" }}>Total Duration</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dynamicStaff.map((s: any) => {
                                    const weekRecs = weekDays.map(d => getRecord(s.id, d));
                                    const weekHrs = weekRecs.reduce((a, r) => a + (r?.totalHours || 0), 0);
                                    return (
                                        <TableRow key={s.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: s.color.bg, color: s.color.fg, fontSize: "0.62rem", fontWeight: 700 }}>{s.avatar}</Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: "#1A2B45", lineHeight: 1.2 }}>{s.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.65rem" }}>{s.role}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {weekDays.map((d, di) => {
                                                const rec = weekRecs[di];
                                                return (
                                                    <TableCell key={d.toString()} align="center"
                                                        sx={{ bgcolor: isToday(d) ? "rgba(0,119,182,0.03)" : "transparent", py: 1.5 }}>
                                                        <StatusDot rec={rec} onClick={rec && rec.status !== "OFF" ? () => openSingleEdit(rec) : undefined} />
                                                        {rec?.totalHours && (
                                                            <Typography variant="caption" sx={{ fontSize: "0.58rem", color: "#94A3B8", display: "block", mt: 0.25 }}>
                                                                {rec.totalHours}h
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight={800} sx={{ color: "#0077B6" }}>
                                                    {formatDurationHrsMins(weekHrs)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {/* ══════════════════ MONTHLY VIEW ══════════════════ */}
                {view === "monthly" && (
                    <Box sx={{ overflowX: "auto" }}>
                        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {(Object.entries(STATUS_CFG) as any[]).map(([k, v]: any) => (
                                <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: v.chipBg, border: `1.5px solid ${v.fg}` }} />
                                    <Typography variant="caption" sx={{ color: "#5C6F85", fontWeight: 600, fontSize: "0.68rem" }}>{v.label}</Typography>
                                </Box>
                            ))}
                            <Typography variant="caption" sx={{ color: "#94A3B8", ml: "auto", fontSize: "0.68rem" }}>
                                {format(selectedDate, "MMMM yyyy")}
                            </Typography>
                        </Box>

                        <Table size="small">
                            <TableHead sx={{
                                "& .MuiTableCell-head": {
                                    color: "#434445ff !important",


                                }
                            }}>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#5C6F85", textTransform: "uppercase", minWidth: 150, position: "sticky", left: 0, bgcolor: "#F8FAFC", zIndex: 1 }}>Staff</TableCell>
                                    {monthDays.map(d => (
                                        <TableCell key={d.toString()} align="center"
                                            sx={{
                                                fontWeight: 700, fontSize: "0.65rem", color: isToday(d) ? "#0077B6" : "#5C6F85",
                                                minWidth: 34, p: "4px 2px",
                                                bgcolor: isToday(d) ? "rgba(0,119,182,0.06)" : "transparent"
                                            }}>
                                            <Box sx={{ fontSize: "0.6rem" }}>{format(d, "EEE")}</Box>
                                            <Box sx={{ fontWeight: 800, color: isToday(d) ? "#0077B6" : "#1A2B45", fontSize: "0.75rem" }}>{format(d, "d")}</Box>
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#5C6F85", textTransform: "uppercase", minWidth: 64, position: "sticky", right: 0, bgcolor: "#F8FAFC", zIndex: 1 }}>Total Duration</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dynamicStaff.map((s: any) => {
                                    const monthRecs = monthDays.map(d => getRecord(s.id, d));
                                    const monthHrs = monthRecs.reduce((a, r) => a + (r?.totalHours || 0), 0);
                                    const attendance = monthRecs.filter(r => r && r.status !== "OFF" && r.status !== "MISSED").length;
                                    const pct = monthDays.length ? Math.round((attendance / monthDays.length) * 100) : 0;
                                    return (
                                        <TableRow key={s.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                            <TableCell sx={{ position: "sticky", left: 0, bgcolor: "white", zIndex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: s.color, fontSize: "0.55rem", fontWeight: 700 }}>{s.avatar}</Avatar>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={700} sx={{ color: "#1A2B45", fontSize: "0.72rem", display: "block" }}>{s.name}</Typography>
                                                        <LinearProgress variant="determinate" value={pct}
                                                            sx={{
                                                                height: 3, borderRadius: 2, bgcolor: "#E2E8F0", width: 80,
                                                                "& .MuiLinearProgress-bar": { bgcolor: pct >= 80 ? "#16A34A" : pct >= 60 ? "#D97706" : "#DC2626" }
                                                            }} />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {monthDays.map((d, di) => {
                                                const rec = monthRecs[di];
                                                return (
                                                    <TableCell key={d.toString()} align="center" sx={{ p: "4px 2px", bgcolor: isToday(d) ? "rgba(0,119,182,0.03)" : "transparent" }}>
                                                        <StatusDot rec={rec} onClick={rec && rec.status !== "OFF" ? () => openSingleEdit(rec) : undefined} />
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="center" sx={{ position: "sticky", right: 0, bgcolor: "white", zIndex: 1 }}>
                                                <Typography variant="caption" fontWeight={800} sx={{ color: "#0077B6", fontSize: "0.72rem" }}>
                                                    {formatDurationHrsMins(monthHrs)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "#94A3B8", display: "block", fontSize: "0.6rem" }}>
                                                    {pct}%
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            {/* ═══════════════ EDIT DIALOG (single or bulk) ══════════════════ */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={800}>
                            {editRecs.length > 1 ? `Bulk Edit — ${editRecs.length} Records` : "Correct Attendance Record"}
                        </Typography>
                        {editRecs.length === 1 && (
                            <Typography variant="caption" sx={{ color: "#5C6F85" }}>
                                {editRecs[0].caregiverName} · {format(new Date(editRecs[0].date), "MMMM d, yyyy")}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={() => setEditOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider sx={{ my: 1.5 }} />
                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={2.5}>
                        {editRecs.length > 1 && (
                            <>
                                <Alert severity="info" sx={{ borderRadius: "12px" }}>
                                    Saving will apply these values to <strong>all {editRecs.length} selected records</strong> below:
                                </Alert>
                                <Box sx={{
                                    display: "flex", gap: 1, flexWrap: "wrap", maxHeight: 120, overflowY: "auto",
                                    p: 1.5, bgcolor: "#F8FAFC", borderRadius: "10px", border: "1px solid", borderColor: "divider"
                                }}>
                                    {editRecs.map(r => (
                                        <Chip
                                            key={r.id}
                                            avatar={<Avatar sx={{ bgcolor: staffColor(r.avatar) }}>{r.avatar}</Avatar>}
                                            label={r.caregiverName}
                                            size="small"
                                            sx={{ bgcolor: "white", border: "1px solid #E2E8F0", fontWeight: 600 }}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Status</InputLabel>
                            <Select value={editForm.status} label="Status"
                                onChange={e => setEditForm(f => ({ ...f, status: e.target.value as AttendanceStatus }))}
                                sx={{ borderRadius: "10px" }}>
                                {(Object.entries(STATUS_CFG) as [AttendanceStatus, any][]).map(([k, v]) => (
                                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {editForm.status === "OFF" && (
                            <TextField label="Date" type="date" size="small" fullWidth
                                value={editForm.date}
                                onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        )}
                        {editForm.status !== "OFF" && editForm.status !== "MISSED" && (
                            <>
                                <TextField label="Check-In Time" type="datetime-local" size="small" fullWidth required
                                    value={editForm.checkIn}
                                    onChange={e => setEditForm(f => ({ ...f, checkIn: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                                <TextField label="Check-Out Time" type="datetime-local" size="small" fullWidth required
                                    value={editForm.checkOut}
                                    onChange={e => setEditForm(f => ({ ...f, checkOut: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                                {editForm.checkIn && editForm.checkOut && (
                                    <Box sx={{ p: 2, bgcolor: "#F0FDF4", borderRadius: "12px", border: "1px solid #bbf7d0" }}>
                                        <Typography variant="caption" sx={{ color: "#16A34A", fontWeight: 600 }}>Calculated Hours</Typography>
                                        <Typography variant="h5" fontWeight={800} sx={{ color: "#16A34A" }}>
                                            {formatDurationHrsMins(calcHours(new Date(editForm.checkIn).toISOString(), new Date(editForm.checkOut).toISOString()))}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={() => setEditOpen(false)} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button variant="contained" onClick={() => applyEdit(editRecs.map(r => r.id))}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", boxShadow: "none", bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" } }}>
                        {editRecs.length > 1 ? `Save ${editRecs.length} Records` : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══════════════ REPORT DIALOG ══════════════════════════════════ */}
            <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={800}>Attendance Report</Typography>
                        <Typography variant="caption" sx={{ color: "#5C6F85" }}>Select date range to generate summary</Typography>
                    </Box>
                    <IconButton onClick={() => setReportOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider sx={{ my: 1.5 }} />
                <DialogContent>
                    <Stack spacing={3}>
                        {/* Date range + facility */}
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="From"
                                    value={reportFrom}
                                    onChange={(newValue) => {
                                        setReportFrom(newValue);
                                        if (newValue && reportTo && reportTo < newValue) {
                                            setReportTo(newValue);
                                        }
                                    }}
                                    format="dd/MM/yyyy"
                                    slots={{ openPickerIcon: CalendarIcon }}
                                    slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: "10px" } } } }}
                                />
                                <DatePicker
                                    label="To"
                                    value={reportTo}
                                    minDate={reportFrom || undefined}
                                    onChange={(newValue) => {
                                        if (newValue && reportFrom && newValue < reportFrom) {
                                            setReportTo(reportFrom);
                                        } else {
                                            setReportTo(newValue);
                                        }
                                    }}
                                    format="dd/MM/yyyy"
                                    slots={{ openPickerIcon: CalendarIcon }}
                                    slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: "10px" } } } }}
                                />
                            </LocalizationProvider>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Facility</InputLabel>
                                <Select value={reportFacility} label="Facility"
                                    onChange={e => setReportFacility(e.target.value)}
                                    sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="ALL">All Facilities</MenuItem>
                                    {facilityOptions.map(f => (
                                        <MenuItem key={f} value={f}>{f}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <Typography variant="body2" color="textSecondary">
                                Use the filters above to refine the data, then export the report using the buttons below.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button variant="outlined" startIcon={exportPdfLoading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />} onClick={handleExportPdf}
                        disabled={exportPdfLoading || exportExcelLoading}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", borderColor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "rgba(72, 202, 228, 0.08)", borderColor: "#3ab3cc" } }}>
                        {exportPdfLoading ? "Exporting..." : "Export PDF"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={exportExcelLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={handleExportExcel}
                        disabled={exportPdfLoading || exportExcelLoading}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", boxShadow: "none", bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" } }}
                    >
                        {exportExcelLoading ? "Exporting..." : "Export Excel"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AttendanceManagementPage;
